import { useGlobalContext } from "@/hooks/useGlobalContext";
import { useClientTelemetry } from "@/lib/clientTelemetry";

import type { ReactNode } from "react";
import type { ClientTelemetryMetadata } from "@/lib/clientTelemetry";

const ALLOWED_EXTERNAL_PROTOCOLS = new Set(["http:", "https:"]);
const DEFAULT_LINK_CLICK_EVENT_NAME = "safe_external_link_clicked";

type SafeExternalLinkProps = {
  href: string | null | undefined;
  children?: ReactNode;
  className?: string;
  logEventMetadata?: ClientTelemetryMetadata;
  title?: string;
};

export function getSafeExternalHref(href: string | null | undefined): string | null {
  if (typeof href !== "string") {
    return null;
  }

  const trimmedHref = href.trim();
  if (!trimmedHref) {
    return null;
  }

  try {
    const parsedHref = new URL(trimmedHref);
    return ALLOWED_EXTERNAL_PROTOCOLS.has(parsedHref.protocol) ? parsedHref.toString() : null;
  } catch {
    return null;
  }
}

export default function SafeExternalLink({
  href,
  children,
  className,
  logEventMetadata,
  title,
}: SafeExternalLinkProps) {
  const { adAccountId, user } = useGlobalContext();
  const { logEvent } = useClientTelemetry();
  const safeHref = getSafeExternalHref(href);
  if (safeHref == null) {
    if (typeof href !== "string" || href.trim() === "") {
      return null;
    }

    return (
      <span className={className} title={title}>
        {children ?? href}
      </span>
    );
  }

  const handleClick = () => {
    logEvent(DEFAULT_LINK_CLICK_EVENT_NAME, undefined, {
      ...logEventMetadata,
      ad_account_id: adAccountId ?? logEventMetadata?.ad_account_id,
      link: safeHref,
      user_id: user?.id ?? logEventMetadata?.user_id,
    });
  };

  return (
    <a
      href={safeHref}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      onClick={handleClick}
      title={title}
    >
      {children ?? safeHref}
    </a>
  );
}
